import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import React, { useCallback, useMemo, useState } from 'react'
import { Select } from '@/components_deprecated'
import { ALL_ISSUE_RELATIONS_QUERY } from '../queries'

interface RelationOptionNode {
  id: string
  name: string
}

interface RelationNode {
  id: string
  name: string
  required: boolean
  options: { edges: { node: RelationOptionNode }[] }
}

interface AllIssueRelationsData {
  allIssueRelations: { edges: { node: RelationNode }[] }
}

interface UseIssueRelationsResult {
  /** Relations rendered as cells (required + opted-in optionals). */
  visibleRelations: RelationNode[]
  /** Optional relations not yet added — surfaced in the "Add" picker. */
  availableOptionalRelations: RelationNode[]
  setRelationOption(relationId: string, optionId: string | null): void
  /** Reveal an optional relation as a cell. */
  addOptional(relationId: string): void
  /** Hide an optional relation and clear its value. */
  removeOptional(relationId: string): void
}

/**
 * Returns true if every required IssueRelation has an option chosen in
 * `value`. Returns true while relations are still loading so the UI does
 * not flash "invalid" before data arrives.
 */
export const useRequiredRelationsSatisfied = (
  value: Record<string, string | null>
): boolean => {
  const { data, loading } = useQuery<AllIssueRelationsData>(
    ALL_ISSUE_RELATIONS_QUERY
  )
  if (loading || !data) return true
  return data.allIssueRelations.edges
    .map(e => e.node)
    .filter(r => r.required)
    .every(r => !!value[r.id])
}

/**
 * Hook that fetches IssueRelations and returns the data + helpers needed by
 * forms.
 *
 * Required relations are always visible. Optional relations are visible only
 * when the user has explicitly added them or when an existing value is
 * present (e.g. editing an issue that already has a chosen option).
 */
export const useIssueRelations = (
  value: Record<string, string | null>,
  onChange: (next: Record<string, string | null>) => void
): UseIssueRelationsResult => {
  const { data } = useQuery<AllIssueRelationsData>(ALL_ISSUE_RELATIONS_QUERY)
  const relations = useMemo(
    () => data?.allIssueRelations.edges.map(e => e.node) ?? [],
    [data]
  )

  // Tracks which optional relations the user has explicitly revealed.
  const [addedOptionalIds, setAddedOptionalIds] = useState<Set<string>>(
    new Set()
  )

  const setRelationOption = useCallback(
    (relationId: string, optionId: string | null) => {
      onChange({ ...value, [relationId]: optionId })
    },
    [value, onChange]
  )

  const addOptional = useCallback((relationId: string) => {
    setAddedOptionalIds(prev => {
      if (prev.has(relationId)) return prev
      const next = new Set(prev)
      next.add(relationId)
      return next
    })
  }, [])

  const removeOptional = useCallback(
    (relationId: string) => {
      setAddedOptionalIds(prev => {
        if (!prev.has(relationId)) return prev
        const next = new Set(prev)
        next.delete(relationId)
        return next
      })
      if (value[relationId]) {
        onChange({ ...value, [relationId]: null })
      }
    },
    [value, onChange]
  )

  const visibleRelations = useMemo(
    () =>
      relations.filter(
        r => r.required || addedOptionalIds.has(r.id) || !!value[r.id]
      ),
    [relations, addedOptionalIds, value]
  )

  const availableOptionalRelations = useMemo(
    () =>
      relations.filter(
        r => !r.required && !addedOptionalIds.has(r.id) && !value[r.id]
      ),
    [relations, addedOptionalIds, value]
  )

  return {
    visibleRelations,
    availableOptionalRelations,
    setRelationOption,
    addOptional,
    removeOptional,
  }
}

/**
 * Render a single relation as a dropdown. Caller wraps it in a grid cell so
 * the visual style matches surrounding fields.
 *
 * The deprecated Select renders this DOM:
 *   .--select-display
 *     .--select-display-inner > .--select-display-selected (label span)
 *     .--select-arrow-icon
 * Ellipsis CSS for these classes lives on the parent MainInfo/CreateMainInfo
 * Wrapper so all selects in the grid share the rules; we only render the
 * Select itself here.
 *
 * `nullable={false}` is intentional: clearing the value is done via the
 * outer remove button on optional relations, and required relations must
 * always have a value. Keeping the Select non-nullable also prevents its
 * built-in clear button from crowding the display row.
 */
interface RelationFieldProps {
  relation: RelationNode
  value: string | null
  onChange(optionId: string | null): void
}

export const IssueRelationField: React.FC<RelationFieldProps> = ({
  relation,
  value,
  onChange,
}) => {
  const translations = useTranslate({ select: 'common.select' })

  const options = relation.options.edges.map(e => ({
    label: e.node.name,
    value: e.node.id,
  }))

  return (
    <Select
      value={value}
      options={options}
      onChange={(id: string | null) => onChange(id)}
      placeholder={translations.select}
      fullWidth
      grayed
      height="3rem"
      nullable={false}
    />
  )
}

/** Build initial form value from an existing issue's relation_options edges. */
export const buildInitialRelationOptions = (
  edges?: { node: { id: string; relation: { id: string } } }[]
): Record<string, string | null> => {
  const out: Record<string, string | null> = {}
  for (const edge of edges ?? []) {
    out[edge.node.relation.id] = edge.node.id
  }
  return out
}

/** Build mutation payload: list of selected IssueRelationOption ids. */
export const serializeRelationOptions = (
  value: Record<string, string | null>
): string[] => Object.values(value).filter((v): v is string => !!v)
